import { fromMarkdown } from "mdast-util-from-markdown";
import type { AssistantTranscriptTimelineItem } from "../types";

/**
 * One option under a completed answer's `Next steps` heading: the letter the model labelled
 * it with and the step text without the `A:` prefix, still markdown (a step may carry a
 * mention link).
 */
export interface NextStepsReplyOption {
  letter: string;
  text: string;
}

/**
 * The answer split at its option block: everything above the options, the options themselves,
 * and whatever the model wrote below them (`""` when it wrote nothing).
 */
export interface NextStepsReplies {
  body: string;
  options: NextStepsReplyOption[];
  tail: string;
}

/** The block's whole text: the label, with an optional colon, and nothing else. */
const NEXT_STEPS_LABEL = /^next\s+steps?:?$/i;
/**
 * `A: step`, and the same with the letter or the letter-and-colon wrapped in emphasis, which is
 * how the model tends to write it (`**A:** step`, `**A**: step`, `_A:_ step`).
 */
const OPTION_ITEM =
  /^(?:\*\*|__|\*|_)?([A-Z])(?:(?:\*\*|__|\*|_)?:|:(?:\*\*|__|\*|_)?)(?:\s+(.*)|$)/;
/** The whole item wrapped rather than the letter: `*A: step*`, `**A: step**`. */
const WHOLE_ITEM_EMPHASIS = /^(\*{1,3}|_{1,3})([\s\S]+)\1$/;
/** A line break and the indentation around it: what folds a wrapped item back into one line. */
const WRAPPED_LINE = /[ \t]*\r?\n[ \t]*/g;
const LEADING_BLANK_LINES = /^(?:[ \t]*\r?\n)+/;

type MarkdownRoot = ReturnType<typeof fromMarkdown>;
type MarkdownNode = MarkdownRoot["children"][number];

/** One option block: the node it came from, and one line of source per item. */
interface OptionBlock {
  node: MarkdownNode;
  items: string[];
}

function childrenOf(node: MarkdownRoot | MarkdownNode): readonly MarkdownNode[] {
  return "children" in node ? node.children : [];
}

/** The literal text of an inline tree, with its markup dropped. */
function flattenText(node: MarkdownNode): string {
  if (node.type === "text") {
    return node.value;
  }
  return childrenOf(node).map(flattenText).join("");
}

/** Text and the emphasis around it, and nothing else: no link, no code span, no raw HTML. */
function isPlainInline(nodes: readonly MarkdownNode[]): boolean {
  return nodes.every(
    (node) =>
      node.type === "text" ||
      ((node.type === "emphasis" || node.type === "strong") && isPlainInline(childrenOf(node)))
  );
}

/**
 * The block that announces the options: a heading of any level, or a paragraph carrying the label
 * and nothing else, emphasised or bare. A block holding anything but text and emphasis is prose
 * about the label or markup the reader cannot see, and neither announces a choice.
 */
function isLabel(node: MarkdownNode): boolean {
  if (node.type !== "heading" && node.type !== "paragraph") {
    return false;
  }
  return isPlainInline(node.children) && NEXT_STEPS_LABEL.test(flattenText(node).trim());
}

/**
 * Inline source that runs across lines: raw HTML, or a code span whose backticks sit on different
 * lines. Every line it swallows reads as prose the reader never sees, so a block carrying one
 * cannot be trusted to say what a click will do. On a single line it hides nothing — a step may
 * quote a command, or emphasise a word in HTML, and still be a choice.
 */
function hidesLines(node: MarkdownNode): boolean {
  const position = node.position;
  if (
    (node.type === "html" || node.type === "inlineCode") &&
    position !== undefined &&
    position.start.line !== position.end.line
  ) {
    return true;
  }
  return childrenOf(node).some(hidesLines);
}

/** A link reference definition anywhere in the document, at any depth. */
function containsDefinition(node: MarkdownRoot | MarkdownNode): boolean {
  if (node.type === "definition") {
    return true;
  }
  return childrenOf(node).some(containsDefinition);
}

/** The original source the node was parsed from, never a re-serialisation of it. */
function sourceOf(source: string, node: MarkdownNode): string | null {
  const start = node.position?.start.offset;
  const end = node.position?.end.offset;
  if (start === undefined || end === undefined) {
    return null;
  }
  return source.slice(start, end);
}

/**
 * Unwraps an item the model emphasised whole. A wrapper carrying its own delimiter inside it is
 * not one: `**A:** do the **work**` opens and closes with `**` without being wrapped, and
 * stripping those would take the closing `**` off `work`.
 */
function stripItemEmphasis(content: string): string {
  const match = WHOLE_ITEM_EMPHASIS.exec(content);
  const delimiter = match?.[1];
  const inner = match?.[2];
  if (delimiter === undefined || inner === undefined || inner.includes(delimiter)) {
    return content;
  }
  return inner.trim();
}

function readOption(content: string): NextStepsReplyOption | null {
  const match = OPTION_ITEM.exec(stripItemEmphasis(content.trim()));
  if (match === null) {
    return null;
  }
  return { letter: match[1] ?? "", text: (match[2] ?? "").trim() };
}

/**
 * The source of every item in a list, one line each, or `null` when the list is not a plain run of
 * steps: an item holding a nested list, a second paragraph or a hidden span carries an instruction
 * that a single button cannot stand for. A wrapped item keeps its markup — the continuation is
 * folded into the line rather than cut off it.
 */
function readListItems(items: readonly MarkdownNode[], source: string): string[] | null {
  const texts: string[] = [];
  for (const item of items) {
    if (item.type !== "listItem" || item.children.length !== 1) {
      return null;
    }
    const [paragraph] = item.children;
    if (paragraph === undefined || paragraph.type !== "paragraph" || hidesLines(paragraph)) {
      return null;
    }
    const raw = sourceOf(source, paragraph);
    if (raw === null) {
      return null;
    }
    texts.push(raw.replace(WRAPPED_LINE, " ").trim());
  }
  return texts;
}

/**
 * The bare form: a paragraph whose every line is an `A: …` line, with no list marker. Every line
 * has to carry a letter, so an ordinary paragraph under the label stays prose and the list below
 * it stays the candidate.
 */
/**
 * A paragraph opens a bare candidate when its first line is an option line. From then on the
 * paragraph is taken whole or not at all: a candidate that fails to read ends the search rather
 * than letting a later list supply the letters.
 */
function startsBareCandidate(paragraph: MarkdownNode, source: string): boolean {
  const firstLine = sourceOf(source, paragraph)?.split(/\r?\n/)[0]?.trim() ?? "";
  return readOption(firstLine) !== null;
}

/**
 * Any inline construct written across a line break, which a per-line split would cut in two.
 * Plain text is exempt: a soft break inside a text node is exactly the line boundary the bare
 * form is split on.
 */
function spansLines(node: MarkdownNode): boolean {
  return childrenOf(node).some(
    (child) =>
      (child.type !== "text" &&
        child.position !== undefined &&
        child.position.start.line !== child.position.end.line) ||
      spansLines(child)
  );
}

/**
 * The bare form is accepted only when every line is its own option and no inline construct
 * crosses a line break: a wrapped item or a link written over two lines would otherwise be read
 * as a second choice, so such a paragraph renders as the prose it is.
 */
function readBareItems(paragraph: MarkdownNode, source: string): string[] | null {
  const raw = sourceOf(source, paragraph);
  if (raw === null || spansLines(paragraph)) {
    return null;
  }
  const texts = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (texts.length === 0 || texts.some((line) => readOption(line) === null)) {
    return null;
  }
  return texts;
}

/**
 * The option block under the label: the first list after it, or the first paragraph written as
 * bare `A: …` lines. Prose, code, raw HTML and quotes above it are body; a heading above it ends
 * the section without options. The first block is the only candidate — a later list describes a
 * different set of instructions, so one that cannot be read as steps leaves the whole section
 * plain rather than handing the letters on.
 */
function findOptionBlock(
  root: MarkdownRoot,
  source: string,
  labelIndex: number
): OptionBlock | null {
  for (const node of root.children.slice(labelIndex + 1)) {
    if (node.type === "heading") {
      return null;
    }
    if (node.type === "list") {
      const items = readListItems(node.children, source);
      return items === null ? null : { node, items };
    }
    if (node.type !== "paragraph" || !startsBareCandidate(node, source)) {
      continue;
    }
    const items = readBareItems(node, source);
    return items === null ? null : { node, items };
  }
  return null;
}

/**
 * Splits a completed answer into the prose above its options, the options, and the prose below.
 *
 * The structure comes from the parsed document, so the model's formatting decides nothing on its
 * own. The label is the last heading or paragraph whose whole text is `Next step(s)`, at any
 * level and in any emphasis; the option block is the first list under it, or the first paragraph
 * of bare `A: …` lines; everything before that block is `body` and everything after it is `tail`,
 * both sliced out of the original source. Two options is the lower bound because a single path is
 * prose, not a choice; there is no upper bound, since a model that wrote six of them still wrote
 * six usable replies.
 *
 * Whole or nothing: the first block is the only candidate, every one of its items has to be a
 * single paragraph reading `A: …`, and a document carrying link reference definitions stays
 * whole. Anything else leaves the section as the plain markdown the model wrote, so no click ever
 * stands for an instruction the reader cannot see or the parser could not keep intact.
 */
export function parseNextStepsReplies(markdown: string): NextStepsReplies | null {
  const root = fromMarkdown(markdown);

  // The three fragments are rendered as three documents, so a definition would sit in one of them
  // while the references that need it sit in the others, and prose outside the choices would lose
  // its links for as long as the answer is interactive.
  if (containsDefinition(root)) {
    return null;
  }

  // The last label wins: an earlier one describes a moment the answer has already moved past.
  let labelIndex = -1;
  root.children.forEach((node, index) => {
    if (isLabel(node)) {
      labelIndex = index;
    }
  });
  if (labelIndex === -1) {
    return null;
  }

  const block = findOptionBlock(root, markdown, labelIndex);
  const start = block?.node.position?.start.offset;
  const end = block?.node.position?.end.offset;
  if (block === null || start === undefined || end === undefined) {
    return null;
  }

  const options: NextStepsReplyOption[] = [];
  for (const item of block.items) {
    const option = readOption(item);
    if (option === null) {
      return null;
    }
    options.push(option);
  }

  if (options.length < 2) {
    return null;
  }
  if (options.some((option) => option.text.length === 0)) {
    return null;
  }
  if (new Set(options.map((option) => option.letter)).size !== options.length) {
    return null;
  }

  return {
    body: markdown.slice(0, start).trimEnd(),
    options,
    tail: markdown.slice(end).replace(LEADING_BLANK_LINES, "").trimEnd(),
  };
}

/** The message a click on option `letter` sends. The one place this sentence lives. */
export function buildNextStepsReplyMessage(letter: string): string {
  return `Please do ${letter}`;
}

/**
 * The key of the answer whose options are live, or `null` when nothing in the thread has any.
 *
 * Only the newest completed answer offers replies, and only while it is still the last thing in
 * the thread: run-control and compaction dividers are chrome around a turn rather than a turn,
 * so they are stepped over, and anything else after the answer means the options describe a
 * moment that has passed. `finality` is optional on this union only because optimistic and
 * streamed rows reach the timeline before the server stamps them, so an unstamped row is by
 * definition not a settled answer and anything but `"final"` disqualifies it.
 */
export function findInteractiveNextStepsMessageKey(
  items: AssistantTranscriptTimelineItem[]
): string | null {
  for (let index = items.length - 1; index >= 0; index--) {
    const item = items[index];
    if (item === undefined) {
      continue;
    }
    if (item.kind === "run_control" || item.kind === "context_compaction") {
      continue;
    }
    if (item.kind !== "message" || item.role !== "assistant") {
      return null;
    }
    if (item.finality !== "final" || item.isStreaming === true) {
      return null;
    }
    return item.key;
  }
  return null;
}
